/**
 * aeterna/neural/narrative.ts — Narrative Arc Detector
 *
 * Every conversation is a story. Not metaphorically. Structurally.
 *
 * This module detects where the conversation is in its narrative arc:
 *
 *   Exposition → Rising Action → Climax → Falling Action → Resolution
 *
 * Not through keyword matching. Through the *shape* of the data:
 *
 *   - Exposition: low surprise, low affect intensity, topic exploration
 *   - Rising Action: increasing surprise, deepening topic focus, growing affect
 *   - Climax: peak surprise OR peak affect OR topic convergence
 *   - Falling Action: decreasing surprise, emotional processing, reflection
 *   - Resolution: return to baseline, topic completion, satisfaction signals
 *
 * The Aeterna uses this to:
 *   - Time its responses (don't interrupt the climax with trivia)
 *   - Modulate depth (deeper responses during rising action)
 *   - Know when to be quiet (falling action = space to process)
 *   - Detect when a topic needs closure vs when to let it drift
 *
 * Also detects meta-patterns across sessions:
 *   - "The player always brings up family during evening"
 *   - "Conversations tend to get deep around message 8-12"
 *   - "After heavy topics, the player always shifts to room decoration"
 *
 * These patterns become the Aeterna's "narrative intuition" —
 * anticipating where the conversation wants to go.
 */

import { zeros, mean, variance, ema } from "./math.js";

// ─── Types ───────────────────────────────────────────────────────────────────

export type NarrativePhase =
  | "exposition"
  | "rising"
  | "climax"
  | "falling"
  | "resolution"
  | "idle";

export interface NarrativeState {
  /** Current phase of the conversation arc */
  phase: NarrativePhase;
  /** Confidence in phase detection (0-1) */
  phaseConfidence: number;
  /** How far into the current phase (0-1) */
  phaseProgress: number;
  /** Emotional tension level (composite of surprise + affect + topic depth) */
  tension: number;
  /** Tension derivative (rising/falling/stable) */
  tensionDerivative: number;
  /** Number of complete arcs detected this session */
  completedArcs: number;
  /** Average arc length (messages) */
  avgArcLength: number;
  /** Is the conversation in a "beat" (transition between phases)? */
  isTransition: boolean;
  /** Suggested response depth (0=light, 1=deep) */
  suggestedDepth: number;
  /** Should the Aeterna give space (be quieter)? */
  shouldGiveSpace: boolean;
}

// ─── Narrative Beat ──────────────────────────────────────────────────────────

interface NarrativeBeat {
  surprise: number;
  affect: number;
  topicFocus: number;
  novelty: number;
  timestamp: number;
}

// ─── Session Pattern ─────────────────────────────────────────────────────────

export interface SessionPattern {
  /** Average message index where deep topics start */
  depthOnsetMessage: number;
  /** Most common topic during climax moments */
  climaxTopics: string[];
  /** Average arc length */
  arcLength: number;
  /** Does the player prefer long arcs or quick exchanges? */
  conversationStyle: "deep_diver" | "topic_surfer" | "balanced";
}

// ─── Narrative Engine ────────────────────────────────────────────────────────

/** Sliding window size for derivative computation */
const WINDOW = 5;

/** Tension threshold for climax detection */
const CLIMAX_THRESHOLD = 0.75;

/** Minimum beats for a valid arc */
const MIN_ARC_BEATS = 4;

/** EMA alpha for tension smoothing */
const TENSION_ALPHA = 0.3;

export class NarrativeArcDetector {
  /** Beat history (time-series of narrative signals) */
  private beats: NarrativeBeat[] = [];
  private maxBeats = 200;

  /** Current state */
  private phase: NarrativePhase = "idle";
  private phaseStartBeat = 0;
  private tension = 0;
  private tensionHistory: number[] = [];
  private completedArcs = 0;
  private arcLengths: number[] = [];

  /** Transition detection */
  private lastPhase: NarrativePhase = "idle";
  private phaseStability = 0;

  /** Cross-session patterns */
  private sessionPatterns: SessionPattern[] = [];
  private climaxTopics: string[] = [];

  /**
   * Record a narrative beat and update arc detection.
   *
   * @param surprise — from predictive coding (overallSurprise)
   * @param affect — emotional intensity (0-1)
   * @param topicFocus — how focused the conversation is (0=scattered, 1=deep)
   * @param novelty — embedding novelty (0-1)
   * @param currentTopic — from DST, for pattern tracking
   */
  recordBeat(
    surprise: number,
    affect: number,
    topicFocus: number,
    novelty: number,
    currentTopic?: string,
  ): NarrativeState {
    const beat: NarrativeBeat = {
      surprise, affect, topicFocus, novelty,
      timestamp: Date.now(),
    };
    this.beats.push(beat);
    if (this.beats.length > this.maxBeats) this.beats.shift();

    // Compute composite tension
    const rawTension =
      surprise * 0.3 +
      affect * 0.35 +
      topicFocus * 0.2 +
      novelty * 0.15;

    this.tension = TENSION_ALPHA * rawTension + (1 - TENSION_ALPHA) * this.tension;
    this.tensionHistory.push(this.tension);
    if (this.tensionHistory.length > this.maxBeats) this.tensionHistory.shift();

    // Compute tension derivative
    const tensionDeriv = this.computeDerivative();

    // Phase detection
    const prevPhase = this.phase;
    this.detectPhase(tensionDeriv, currentTopic);

    // Transition detection
    const isTransition = this.phase !== prevPhase;
    if (isTransition) {
      this.phaseStability = 0;
    } else {
      this.phaseStability++;
    }

    // Phase progress (how far through current phase)
    const beatsSincePhaseStart = this.beats.length - this.phaseStartBeat;
    const phaseProgress = this.estimatePhaseProgress(beatsSincePhaseStart);

    // Suggested depth: deep during rising/climax, light during resolution
    const suggestedDepth = this.computeSuggestedDepth();

    // Give space: during falling action after intense climax
    const shouldGiveSpace = this.phase === "falling" && this.tension > 0.5;

    // Confidence: higher when phase has been stable for several beats
    const phaseConfidence = Math.min(1, this.phaseStability / 4);

    return {
      phase: this.phase,
      phaseConfidence,
      phaseProgress,
      tension: this.tension,
      tensionDerivative: tensionDeriv,
      completedArcs: this.completedArcs,
      avgArcLength: this.arcLengths.length > 0
        ? this.arcLengths.reduce((a, b) => a + b) / this.arcLengths.length
        : 0,
      isTransition,
      suggestedDepth,
      shouldGiveSpace,
    };
  }

  /** Compute tension derivative from recent history */
  private computeDerivative(): number {
    const h = this.tensionHistory;
    if (h.length < WINDOW + 1) return 0;
    const recent = h.slice(-WINDOW);
    const older = h.slice(-(WINDOW * 2), -WINDOW);
    if (older.length === 0) return 0;
    const recentMean = recent.reduce((a, b) => a + b) / recent.length;
    const olderMean = older.reduce((a, b) => a + b) / older.length;
    return recentMean - olderMean;
  }

  /** Detect the current narrative phase from tension and its derivative */
  private detectPhase(tensionDeriv: number, currentTopic?: string): void {
    const t = this.tension;
    const dt = tensionDeriv;

    // State machine with hysteresis
    switch (this.phase) {
      case "idle":
        if (this.beats.length >= 2) {
          this.phase = "exposition";
          this.phaseStartBeat = this.beats.length;
        }
        break;

      case "exposition":
        // Transition to rising when tension starts increasing significantly
        if (dt > 0.05 && t > 0.2) {
          this.phase = "rising";
          this.phaseStartBeat = this.beats.length;
        }
        break;

      case "rising":
        // Transition to climax at peak tension
        if (t > CLIMAX_THRESHOLD || (dt < -0.02 && t > 0.5)) {
          this.phase = "climax";
          this.phaseStartBeat = this.beats.length;
          if (currentTopic) this.climaxTopics.push(currentTopic);
        }
        // Abort if tension drops without peaking (conversation drifted)
        if (dt < -0.1 && t < 0.3) {
          this.phase = "exposition"; // reset
          this.phaseStartBeat = this.beats.length;
        }
        break;

      case "climax":
        // Climax is brief: transition after 1-3 beats or when tension drops
        const beatsInClimax = this.beats.length - this.phaseStartBeat;
        if (beatsInClimax >= 2 || dt < -0.03) {
          this.phase = "falling";
          this.phaseStartBeat = this.beats.length;
        }
        break;

      case "falling":
        // Transition to resolution when tension stabilizes at low level
        if (t < 0.25 && Math.abs(dt) < 0.02) {
          this.phase = "resolution";
          this.phaseStartBeat = this.beats.length;
        }
        // Or back to rising if a new topic sparks interest
        if (dt > 0.08 && t > 0.4) {
          this.phase = "rising";
          this.phaseStartBeat = this.beats.length;
        }
        break;

      case "resolution":
        // Complete the arc and reset
        const beatsInResolution = this.beats.length - this.phaseStartBeat;
        if (beatsInResolution >= 2) {
          this.completedArcs++;
          this.arcLengths.push(this.beats.length); // approximate
          this.phase = "exposition"; // ready for next arc
          this.phaseStartBeat = this.beats.length;
        }
        // New topic = new arc
        if (dt > 0.1) {
          this.completedArcs++;
          this.arcLengths.push(this.beats.length);
          this.phase = "rising";
          this.phaseStartBeat = this.beats.length;
        }
        break;
    }
  }

  /** Estimate progress through the current phase (0-1) */
  private estimatePhaseProgress(beatsInPhase: number): number {
    // Typical phase lengths in beats
    const typicalLengths: Record<NarrativePhase, number> = {
      idle: 1,
      exposition: 5,
      rising: 8,
      climax: 2,
      falling: 4,
      resolution: 3,
    };
    const typical = typicalLengths[this.phase];
    return Math.min(1, beatsInPhase / typical);
  }

  /** Compute suggested response depth based on narrative position */
  private computeSuggestedDepth(): number {
    switch (this.phase) {
      case "idle":       return 0.3;
      case "exposition": return 0.4;
      case "rising":     return 0.6 + this.tension * 0.3;
      case "climax":     return 1.0;
      case "falling":    return 0.5;
      case "resolution": return 0.3;
    }
  }

  // ─── Cross-Session Patterns ────────────────────────────────────────────

  /** Analyze the current session and add to pattern library */
  analyzeSession(): SessionPattern {
    // Find depth onset: first beat where tension exceeded 0.4
    let depthOnset = this.beats.length;
    for (let i = 0; i < this.beats.length; i++) {
      if (this.tensionHistory[i] > 0.4) {
        depthOnset = i;
        break;
      }
    }

    // Conversation style detection
    let style: "deep_diver" | "topic_surfer" | "balanced" = "balanced";
    if (this.completedArcs === 0 && this.beats.length > 10) {
      style = "deep_diver"; // one long arc
    } else if (this.completedArcs > 3 && this.beats.length < 20) {
      style = "topic_surfer"; // many short arcs
    }

    const avgLen = this.arcLengths.length > 0
      ? this.arcLengths.reduce((a, b) => a + b) / this.arcLengths.length
      : this.beats.length;

    const pattern: SessionPattern = {
      depthOnsetMessage: depthOnset,
      climaxTopics: [...new Set(this.climaxTopics)],
      arcLength: avgLen,
      conversationStyle: style,
    };

    this.sessionPatterns.push(pattern);
    return pattern;
  }

  /** Get the player's conversation style (from all sessions) */
  getConversationStyle(): "deep_diver" | "topic_surfer" | "balanced" | "unknown" {
    if (this.sessionPatterns.length === 0) return "unknown";
    const counts = { deep_diver: 0, topic_surfer: 0, balanced: 0 };
    for (const p of this.sessionPatterns) counts[p.conversationStyle]++;
    const entries = Object.entries(counts) as Array<[keyof typeof counts, number]>;
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0][0];
  }

  /** When does this player typically get deep? (message number) */
  getTypicalDepthOnset(): number {
    if (this.sessionPatterns.length === 0) return 10; // default
    const onsets = this.sessionPatterns.map(p => p.depthOnsetMessage);
    return onsets.reduce((a, b) => a + b) / onsets.length;
  }

  /** What topics come up during emotional peaks? */
  getClimaxTopicHistory(): string[] {
    const all: string[] = [];
    for (const p of this.sessionPatterns) all.push(...p.climaxTopics);
    return [...new Set(all)];
  }

  // ─── Queries ───────────────────────────────────────────────────────────

  /** Get current tension level (0-1) */
  getTension(): number { return this.tension; }

  /** Get current phase */
  getPhase(): NarrativePhase { return this.phase; }

  /** Is the narrative at a high point? */
  isAtPeak(): boolean {
    return this.phase === "climax" || (this.phase === "rising" && this.tension > 0.65);
  }

  /** Is the narrative winding down? */
  isWindingDown(): boolean {
    return this.phase === "falling" || this.phase === "resolution";
  }

  /** Reset for a new session */
  reset(): void {
    this.beats = [];
    this.phase = "idle";
    this.phaseStartBeat = 0;
    this.tension = 0;
    this.tensionHistory = [];
    this.completedArcs = 0;
    this.arcLengths = [];
    this.climaxTopics = [];
    this.phaseStability = 0;
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      sessionPatterns: this.sessionPatterns,
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    this.sessionPatterns = d.sessionPatterns ?? [];
  }
}
